import type { Downloads, Follows, Markets, News } from '~api';
import { api } from '~portal/api';
import { logger } from '~portal/lib/logger';
import type { LiveQuery } from './types';

/**
 * Live data for the operators surfaces (UI round 2, Lane D): the company page's news and downloads,
 * the caller's follows, and the markets directory the listing's
 * location filter and the regions carousel resolve slugs against. Each hook reads a tRPC
 * procedure and returns {@link LiveQuery} — pending, failed or loaded — so a page shows a loading
 * state while the query runs and an inline error when it fails. Nothing here falls back to a
 * fixture on error: a fixture stands in only for fields no procedure supplies, and is badged.
 */

type QueryLike<T> = {
  data?: T | undefined;
  isPending?: boolean;
  isLoading?: boolean;
  error?: { message: string } | null;
};

const live = <T>(query: QueryLike<T>): LiveQuery<T> => ({
  data: query.data,
  isLoading: Boolean(query.isPending ?? query.isLoading),
  error: query.error ? { message: query.error.message } : null,
});

// --- Company page ----------------------------------------------------------------------------

/** @desc Stories per page on the company page's "Recent news" (C9). */
export const COMPANY_NEWS_PAGE_SIZE = 6;

/** @desc A page of a group's company news, newest first (Mongo `news`, the legacy backfill). */
export const useGroupNews = (slug: string, page: number): LiveQuery<News.Page> =>
  live(
    api.news.list.useQuery({ kind: 'company', group: slug, page, limit: COMPANY_NEWS_PAGE_SIZE }),
  );

/** @desc A group's documents from the downloads catalogue, newest first (C13). */
export const useGroupDownloads = (slug: string): LiveQuery<Downloads.CatalogueEntry[]> =>
  live(api.downloads.catalogue.useQuery({ group: slug, kind: 'company-document' }));

/**
 * @desc Fetches a short-lived signed link for a catalogue entry and hands the browser to it. A
 * mutation (it records the download), so it runs on the click and never on render.
 */
export const useSignedDownload = () => {
  const mutation = api.downloads.signedUrl.useMutation();
  return {
    isPending: mutation.isPending,
    pendingId: mutation.isPending ? mutation.variables?.id : undefined,
    error: mutation.error ? { message: mutation.error.message } : null,
    download: (id: string, open: (url: string) => void) =>
      mutation.mutate({ id }, { onSuccess: ({ url }) => open(url) }),
  };
};

// --- Follows -----------------------------------------------------------------------------------

export type FollowKey = Pick<Follows.Follow, 'entityType' | 'slug'>;

/** @desc Whether `follows` contains the page. */
export const isFollowed = (follows: readonly FollowKey[] | undefined, key: FollowKey) =>
  Boolean(follows?.some((f) => f.entityType === key.entityType && f.slug === key.slug));

/** @desc `follows` with the page added, or removed if already present — the optimistic twin of `follows.toggle`. */
export const toggledFollows = (
  follows: readonly Follows.Follow[],
  key: FollowKey,
  at = new Date(),
): Follows.Follow[] =>
  isFollowed(follows, key)
    ? follows.filter((f) => !(f.entityType === key.entityType && f.slug === key.slug))
    : [{ ...key, followedAt: at }, ...follows];

/** @desc The caller's tracked pages — what every bookmark reads its state from. */
export const useFollowSet = (): LiveQuery<Follows.Follow[]> => live(api.follows.list.useQuery());

/**
 * @desc Follow or unfollow a page through `follows.toggle`, updating the follows list at once and
 * rolling back if the server disagrees; the list is refetched either way, and so is the activity
 * feed, which a follow adds the caller's own "Page followed" row to (H2-310). `name` is the page's
 * display name, for that row.
 */
export const useToggleFollow = () => {
  const utils = api.useUtils();
  const mutation = api.follows.toggle.useMutation({
    onMutate: async ({ entityType, slug }) => {
      await utils.follows.list.cancel();
      const previous = utils.follows.list.getData();
      utils.follows.list.setData(undefined, (old) =>
        toggledFollows(old ?? [], { entityType, slug }),
      );
      return { previous };
    },
    onError: (error, { entityType, slug }, context) => {
      utils.follows.list.setData(undefined, context?.previous);
      logger.warn('[Follows] Toggle failed', { error: error.message, entityType, slug });
    },
    onSettled: () => Promise.all([utils.follows.list.invalidate(), utils.activity.invalidate()]),
  });
  return {
    toggle: (key: FollowKey, name?: string) =>
      mutation.mutate({ entityType: key.entityType, slug: key.slug, ...(name ? { name } : {}) }),
    isPending: mutation.isPending,
  };
};

// --- Markets directory -------------------------------------------------------------------------

/** @desc What the operators surfaces need of a market: its name, flag and continent. */
export type MarketRef = {
  slug: string;
  name: string;
  /** @desc An `EntityLogo` flag code (`gb`, `us-nj`), when the market has an ISO code. */
  flagCode?: string;
  continent: Markets.Continent;
  /**
   * @desc Portal group slugs the licensed-entity data credits in this market; present only where
   * the warehouse has licensed-entity rows (Lane M), absent (unknown) everywhere else.
   */
  operatorsPresent?: readonly string[];
};

/** @desc `GB` → `gb`, `US-NJ` → `us-nj`: the contract's ISO code as `EntityLogo` spells a flag. */
export const flagCodeFromIso = (isoCode: string | undefined): string | undefined =>
  isoCode ? isoCode.toLowerCase() : undefined;

export const toMarketRef = (listing: Markets.Listing): MarketRef => {
  const flagCode = flagCodeFromIso(listing.isoCode);
  return {
    slug: listing.slug,
    name: listing.name,
    continent: listing.continent,
    ...(flagCode ? { flagCode } : {}),
    ...(listing.operatorsPresent ? { operatorsPresent: listing.operatorsPresent } : {}),
  };
};

/** @desc Every live market by slug — the whole list in one page, named A–Z. */
export const useMarketDirectory = (): LiveQuery<ReadonlyMap<string, MarketRef>> => {
  const query = api.markets.list.useQuery({
    sort: { by: 'name', dir: 'asc' },
    page: 1,
    limit: 500,
  });
  const data = query.data
    ? new Map(query.data.items.map((item) => [item.slug, toMarketRef(item)]))
    : undefined;
  return { ...live(query), data };
};
